import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Save, Loader2, Globe, ChevronDown } from "lucide-react";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { useSiteContent } from "@/context/SiteContentContext";
import { AdminSellerServicesEditor } from "@/components/AdminSellerServicesEditor";
import { buildSellerServicesPagePayload } from "@/lib/sellerServicesPagePayload";
import { DEFAULT_SELLER_SERVICES_PAGE } from "@/lib/defaultSellerServicesPage";

function normalizeServicesPage(raw) {
  if (raw && Array.isArray(raw.sections) && raw.sections.length > 0) {
    return raw;
  }
  if (raw && raw.hero_title) {
    return { ...DEFAULT_SELLER_SERVICES_PAGE, ...raw, sections: raw.sections?.length ? raw.sections : DEFAULT_SELLER_SERVICES_PAGE.sections };
  }
  return JSON.parse(JSON.stringify(DEFAULT_SELLER_SERVICES_PAGE));
}

function Field({ label, value, onChange, multiline, testid }) {
  const cls =
    "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:border-electric outline-none";
  return (
    <label className="block text-xs text-slate-400 mb-3">
      {label}
      {multiline ? (
        <textarea
          data-testid={testid}
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${cls} mt-1 resize-y min-h-[72px]`}
        />
      ) : (
        <input
          data-testid={testid}
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${cls} mt-1`}
        />
      )}
    </label>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass rounded-2xl border border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03]"
      >
        <span className="font-heading text-base">{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/5 space-y-1">{children}</div>}
    </div>
  );
}

function setDeep(obj, path, value) {
  const keys = path.split(".");
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
}

export function AdminSiteContent() {
  const { refresh } = useSiteContent();
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE_CONTENT)));
  const [servicesPage, setServicesPage] = useState(() =>
    JSON.parse(JSON.stringify(DEFAULT_SELLER_SERVICES_PAGE)),
  );
  const [pricingLoadFailed, setPricingLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setPricingLoadFailed(false);
    try {
      const sc = await api.get("/site-content");
      if (sc.data?.site_content) setSite(sc.data.site_content);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Weboldal tartalom betöltése sikertelen");
    }
    try {
      const pc = await api.get("/pricing-config");
      setServicesPage(normalizeServicesPage(pc.data?.seller_services_page));
    } catch (e) {
      setPricingLoadFailed(true);
      setServicesPage(JSON.parse(JSON.stringify(DEFAULT_SELLER_SERVICES_PAGE)));
      toast.error(
        e.response?.status === 404
          ? "Árazás API nem elérhető (404) — indítsd a backendet, majd frissíts."
          : e.response?.data?.detail || "Szolgáltatások oldal betöltése sikertelen",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (path, value) => setSite((prev) => setDeep(prev, path, value));

  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = {
        site_content: site,
        seller_services_page: buildSellerServicesPagePayload(servicesPage),
      };
      const r = await api.patch("/admin/site-content", payload);
      if (r.data?.site_content) setSite(r.data.site_content);
      if (r.data?.seller_services_page) setServicesPage(r.data.seller_services_page);
      toast.success("Weboldal tartalom mentve");
      refresh().catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 text-electric animate-spin" />
      </div>
    );
  }

  const hero = site.hero || {};
  const inv = site.inventory || {};
  const ai = site.ai_recommender || {};
  const rev = site.site_reviews || {};
  const contact = site.contact || {};
  const chat = site.support_chatbot || {};
  const seo = site.seo || {};

  return (
    <div data-testid="admin-site-content" className="space-y-4">
      <p className="text-xs text-slate-500">
        Weboldal szöveg AI-val: Admin → Asszisztens fül (mentés után frissítsd ezt az oldalt).
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-slate-300">
          <Globe className="w-5 h-5 text-electric" />
          <p className="text-sm text-slate-400 max-w-xl">
            Dizájn, szöveg és struktúra — a kreditárak változatlanok maradnak az Árazás fülön.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-site-content-save"
          disabled={saving}
          onClick={saveAll}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm font-medium"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Összes mentése
        </button>
      </div>

      {pricingLoadFailed && (
        <p className="text-sm text-amber-300/90 mb-3" data-testid="admin-services-load-warn">
          A mentett szolgáltatás-adatok nem jöttek le a szerverről — alapértelmezés látszik. Indítsd a backendet és kattints az
          {" "}
          <button type="button" className="underline text-amber-200" onClick={load}>
            újratöltésre
          </button>
          .
        </p>
      )}
      <div className="mb-4">
        <AdminSellerServicesEditor
          page={servicesPage}
          onChange={setServicesPage}
          onSave={() => saveAll()}
          saving={saving}
        />
      </div>

      <Section title="SEO és márka" defaultOpen>
        <Field label="Oldal cím (főoldal)" testid="sc-seo-title" value={seo.default_title} onChange={(v) => set("seo.default_title", v)} />
        <Field label="Meta leírás" multiline testid="sc-seo-desc" value={seo.default_description} onChange={(v) => set("seo.default_description", v)} />
        <Field label="Márka — első rész" testid="sc-brand-name" value={site.brand?.name} onChange={(v) => set("brand.name", v)} />
        <Field label="Márka — kiemelt rész" testid="sc-brand-hi" value={site.brand?.name_highlight} onChange={(v) => set("brand.name_highlight", v)} />
      </Section>

      <Section title="Globális sáv (opcionális)">
        <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
          <input
            type="checkbox"
            checked={!!site.global_banner?.enabled}
            onChange={(e) => set("global_banner.enabled", e.target.checked)}
          />
          Banner megjelenítése
        </label>
        <Field label="Banner szöveg" testid="sc-banner-text" value={site.global_banner?.text} onChange={(v) => set("global_banner.text", v)} />
        <Field label="Banner link (opcionális)" testid="sc-banner-href" value={site.global_banner?.href} onChange={(v) => set("global_banner.href", v)} />
      </Section>

      <Section title="Főoldal — Hero" defaultOpen>
        <Field label="Badge" testid="sc-hero-badge" value={hero.badge} onChange={(v) => set("hero.badge", v)} />
        <Field label="Cím — eleje" testid="sc-hero-t1" value={hero.title_before} onChange={(v) => set("hero.title_before", v)} />
        <Field label="Cím — kiemelt" testid="sc-hero-th" value={hero.title_highlight} onChange={(v) => set("hero.title_highlight", v)} />
        <Field label="Cím — vége" testid="sc-hero-t2" value={hero.title_after} onChange={(v) => set("hero.title_after", v)} />
        <Field label="Alcím" multiline testid="sc-hero-sub" value={hero.subtitle} onChange={(v) => set("hero.subtitle", v)} />
        <Field label="CTA 1 felirat" testid="sc-hero-cta1" value={hero.cta_primary_label} onChange={(v) => set("hero.cta_primary_label", v)} />
        <Field label="CTA 2 felirat" testid="sc-hero-cta2" value={hero.cta_secondary_label} onChange={(v) => set("hero.cta_secondary_label", v)} />
        <Field label="Hero videó (mp4 útvonal)" testid="sc-hero-mp4" value={hero.video_mp4} onChange={(v) => set("hero.video_mp4", v)} />
        <Field label="Hero videó (mov útvonal)" testid="sc-hero-mov" value={hero.video_mov} onChange={(v) => set("hero.video_mov", v)} />
        {(hero.stats || []).map((st, i) => (
          <div key={st.id || i} className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 p-3 rounded-xl bg-white/[0.02]">
            <Field label={`Stat ${i + 1} — felirat`} value={st.label} onChange={(v) => {
              const stats = [...(hero.stats || [])];
              stats[i] = { ...stats[i], label: v };
              set("hero.stats", stats);
            }} />
            {!st.dynamic && (
              <Field label={`Stat ${i + 1} — érték`} value={st.value} onChange={(v) => {
                const stats = [...(hero.stats || [])];
                stats[i] = { ...stats[i], value: v };
                set("hero.stats", stats);
              }} />
            )}
          </div>
        ))}
      </Section>

      <Section title="Főoldal — szekciók (sorrend és láthatóság)">
        <p className="text-xs text-slate-500 mb-3">A Hero és a Navbar mindig látszik. Itt a többi blokk kapcsolható.</p>
        {(site.home_sections || []).map((sec, i) => (
          <label key={sec.id} className="flex items-center gap-2 text-sm text-slate-300 py-1">
            <input
              type="checkbox"
              checked={!!sec.enabled}
              onChange={(e) => {
                const next = [...site.home_sections];
                next[i] = { ...next[i], enabled: e.target.checked };
                set("home_sections", next);
              }}
            />
            {sec.id}
          </label>
        ))}
      </Section>

      <Section title="Készlet szekció">
        <Field label="Eyebrow" value={inv.eyebrow} onChange={(v) => set("inventory.eyebrow", v)} />
        <Field label="Cím" value={inv.title} onChange={(v) => set("inventory.title", v)} />
        <Field label="Leírás" multiline value={inv.description} onChange={(v) => set("inventory.description", v)} />
        <Field label="Nincs találat üzenet" multiline value={inv.empty_message} onChange={(v) => set("inventory.empty_message", v)} />
      </Section>

      <Section title="AI tanácsadó szekció">
        <Field label="Cím" value={ai.title} onChange={(v) => set("ai_recommender.title", v)} />
        <Field label="Leírás" multiline value={ai.description} onChange={(v) => set("ai_recommender.description", v)} />
        <Field label="Hibaüzenet" multiline value={ai.error_message} onChange={(v) => set("ai_recommender.error_message", v)} />
        <Field label="Gomb felirat" value={ai.submit_label} onChange={(v) => set("ai_recommender.submit_label", v)} />
      </Section>

      <Section title="Vélemények szekció">
        <Field label="Cím" value={rev.title} onChange={(v) => set("site_reviews.title", v)} />
        <Field label="Leírás" multiline value={rev.description} onChange={(v) => set("site_reviews.description", v)} />
        <Field label="Jutalom hint" multiline value={rev.reward_hint} onChange={(v) => set("site_reviews.reward_hint", v)} />
      </Section>

      <Section title="Kapcsolat">
        <Field label="Cím" value={contact.title} onChange={(v) => set("contact.title", v)} />
        <Field label="Leírás" multiline value={contact.description} onChange={(v) => set("contact.description", v)} />
        {(contact.channels || []).map((ch, i) => (
          <div key={i} className="grid md:grid-cols-2 gap-2 mt-2">
            <Field label={`Csatorna ${i + 1} — típus`} value={ch.label} onChange={(v) => {
              const channels = [...contact.channels];
              channels[i] = { ...channels[i], label: v };
              set("contact.channels", channels);
            }} />
            <Field label="Érték" value={ch.value} onChange={(v) => {
              const channels = [...contact.channels];
              channels[i] = { ...channels[i], value: v };
              set("contact.channels", channels);
            }} />
          </div>
        ))}
        <Field label="Siker cím" value={contact.success_title} onChange={(v) => set("contact.success_title", v)} />
      </Section>

      <Section title="Menü (felső navigáció)">
        {(site.nav?.links || []).map((link, i) => (
          <div key={i} className="grid md:grid-cols-3 gap-2 mb-2 items-end">
            <Field label="Felirat" value={link.label} onChange={(v) => {
              const links = [...site.nav.links];
              links[i] = { ...links[i], label: v };
              set("nav.links", links);
            }} />
            <Field label="Útvonal" value={link.path} onChange={(v) => {
              const links = [...site.nav.links];
              links[i] = { ...links[i], path: v };
              set("nav.links", links);
            }} />
            <label className="flex items-center gap-2 text-xs text-slate-400 pb-2">
              <input
                type="checkbox"
                checked={link.visible !== false}
                onChange={(e) => {
                  const links = [...site.nav.links];
                  links[i] = { ...links[i], visible: e.target.checked };
                  set("nav.links", links);
                }}
              />
              Látható
            </label>
          </div>
        ))}
      </Section>

      <Section title="Lábléc">
        <Field label="Copyright kiegészítő" value={site.footer?.copyright} onChange={(v) => set("footer.copyright", v)} />
        <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
          <input
            type="checkbox"
            checked={site.footer?.show_brand_marquee !== false}
            onChange={(e) => set("footer.show_brand_marquee", e.target.checked)}
          />
          Márkasáv (Brand marquee) megjelenítése
        </label>
        {(site.footer?.links || []).map((link, i) => (
          <div key={i} className="grid md:grid-cols-3 gap-2 mb-2">
            <Field label="Felirat" value={link.label} onChange={(v) => {
              const links = [...site.footer.links];
              links[i] = { ...links[i], label: v };
              set("footer.links", links);
            }} />
            <Field label="Útvonal" value={link.path} onChange={(v) => {
              const links = [...site.footer.links];
              links[i] = { ...links[i], path: v };
              set("footer.links", links);
            }} />
            <label className="flex items-center gap-2 text-xs text-slate-400 pb-2">
              <input
                type="checkbox"
                checked={link.visible !== false}
                onChange={(e) => {
                  const links = [...site.footer.links];
                  links[i] = { ...links[i], visible: e.target.checked };
                  set("footer.links", links);
                }}
              />
              Látható
            </label>
          </div>
        ))}
      </Section>

      <Section title="Chatbot (lebegő)">
        <Field label="Üdvözlő üzenet" multiline value={chat.welcome_message} onChange={(v) => set("support_chatbot.welcome_message", v)} />
        <Field label="Fejléc cím" value={chat.header_title} onChange={(v) => set("support_chatbot.header_title", v)} />
        <Field label="Input placeholder" value={chat.input_placeholder} onChange={(v) => set("support_chatbot.input_placeholder", v)} />
      </Section>

      <Section title="Belépés / kedvencek / autó 404">
        <Field label="Belépés alcím" value={site.auth_page?.subtitle} onChange={(v) => set("auth_page.subtitle", v)} />
        <Field label="Regisztráció banner (HTML)" multiline value={site.auth_page?.welcome_banner_html} onChange={(v) => set("auth_page.welcome_banner_html", v)} />
        <Field label="Kedvencek cím" value={site.favorites_page?.title} onChange={(v) => set("favorites_page.title", v)} />
        <Field label="Autó nem található cím" value={site.car_detail?.not_found_title} onChange={(v) => set("car_detail.not_found_title", v)} />
      </Section>
    </div>
  );
}
